// /////////////////////////////////////////////////////////////
// [ファイル] SfaListColumnsEditor.tsx
// [概要] 営業案件一覧の「表示列」の編集（抽出条件・並び替えの小窓の 3 つ目のタブ）。
//        列を縦に並べ、チェックで出す／出さないを切り替え、左のつまみをドラッグして順番を変える
//        （イベント管理の発表順の並べ替えと同じ @dnd-kit。キーボードはつまみで Space → ↑↓ → Space）。
//        案件名は先頭に固定（チェックを外せず、動かせない）。値はキーの並び（出す列だけ）で親が持つ。
// /////////////////////////////////////////////////////////////
import React, { useMemo } from 'react';
import { Box, Button, Checkbox, Typography } from '@mui/material';
import { DndContext, DragEndEvent, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import * as Icons from '../../constants/icons';
import { STANDARD_TEXT_BUTTON_SX } from '../../constants/buttonStyles';
import { DEFAULT_LIST_COLUMNS, FIXED_LIST_COLUMN, SFA_LIST_COLUMN_OPTIONS, isDefaultListColumns, normalizeListColumns } from './sfaListColumns';

interface RowProps { id: string; label: string; checked: boolean; fixed: boolean; onToggle: () => void }

function SortableColumnRow({ id, label, checked, fixed, onToggle }: RowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: fixed });
  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      sx={{
        display: 'flex', alignItems: 'center', gap: 0.5, px: 0.5, py: 0.25,
        bgcolor: fixed ? '#f1f5f9' : isDragging ? '#f8fafc' : '#fff',
        border: 1, borderColor: isDragging ? '#93c5fd' : 'divider', borderRadius: 1,
        boxShadow: isDragging ? '0 4px 12px rgba(15,23,42,0.15)' : 'none',
        position: 'relative', zIndex: isDragging ? 1 : 'auto',
      }}
    >
      <Box
        {...(fixed ? {} : { ...attributes, ...listeners })}
        aria-label={fixed ? undefined : `${label} を動かす`}
        sx={{ display: 'flex', color: fixed ? '#cbd5e1' : '#94a3b8', cursor: fixed ? 'default' : isDragging ? 'grabbing' : 'grab', touchAction: 'none' }}
      >
        <Icons.DragIndicator sx={{ fontSize: 20 }} />
      </Box>
      <Checkbox size="small" checked={checked} disabled={fixed} onChange={onToggle} sx={{ p: 0.5 }} slotProps={{ input: { 'aria-label': `${label} を表示` } }} />
      <Typography sx={{ fontSize: '13px', flex: 1, color: fixed ? 'text.secondary' : 'text.primary' }}>{label}</Typography>
      {fixed && <Typography variant="caption" color="text.secondary">先頭に固定</Typography>}
    </Box>
  );
}

export default function SfaListColumnsEditor({ value, onChange }: { value: string[]; onChange: (cols: string[]) => void }) {
  // 行の並びは「出す列（設定の順）＋出さない列（定義の順）」。出す列だけが値になる
  const order = useMemo(() => {
    const visible = normalizeListColumns(value);
    return [...visible, ...SFA_LIST_COLUMN_OPTIONS.map((o) => o.key).filter((k) => !visible.includes(k))];
  }, [value]);
  const visible = useMemo(() => new Set(normalizeListColumns(value)), [value]);
  const labelOf = useMemo(() => new Map(SFA_LIST_COLUMN_OPTIONS.map((o) => [o.key, o.label])), []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const emit = (nextOrder: string[], nextVisible: Set<string>) => onChange(nextOrder.filter((k) => nextVisible.has(k)));
  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = order.indexOf(String(active.id));
    const to = order.indexOf(String(over.id));
    if (from < 0 || to <= 0) return; // 先頭（案件名）の上には置けない
    const moved = arrayMove(order, from, to);
    // 出さない列の位置へ動かしたときは、その列も出すようにする（順番を決めた＝出す意図とみなす）
    const nextVisible = new Set(visible);
    nextVisible.add(String(active.id));
    emit(moved, nextVisible);
  };
  const toggle = (key: string) => {
    const nextVisible = new Set(visible);
    if (nextVisible.has(key)) nextVisible.delete(key); else nextVisible.add(key);
    emit(order, nextVisible);
  };

  return (
    <Box sx={{ pt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          チェックで出す列を選び、左のつまみをドラッグして左からの順番を決めます。案件名は先頭に固定です。
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button size="small" startIcon={<Icons.RestartAlt />} sx={{ ...STANDARD_TEXT_BUTTON_SX, flexShrink: 0 }} disabled={isDefaultListColumns(normalizeListColumns(value))} onClick={() => onChange([...DEFAULT_LIST_COLUMNS])}>
          既定に戻す
        </Button>
      </Box>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={order} strategy={verticalListSortingStrategy}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {order.map((k) => (
              <SortableColumnRow key={k} id={k} label={labelOf.get(k) || k} checked={visible.has(k)} fixed={k === FIXED_LIST_COLUMN} onToggle={() => toggle(k)} />
            ))}
          </Box>
        </SortableContext>
      </DndContext>
    </Box>
  );
}
